<template>
  <div v-if="visible" class="dev-tools-panel">
    <!-- Collapsed bubble -->
    <v-btn
      v-if="!panelOpen"
      class="dev-tools-bubble"
      color="#1e1e2e"
      dark
      depressed
      @click="setPanelOpen(true)"
      :aria-label="`Open dev tools (${activeCount} active overrides)`"
    >
      <v-icon left small>mdi-console-line</v-icon>
      DEV
      <span
        v-if="activeCount > 0"
        class="dev-tools-bubble__count"
      >{{ activeCount }}</span>
    </v-btn>

    <!-- Expanded card -->
    <v-card v-else class="dev-tools-card" elevation="24">
      <!-- Header -->
      <header class="dev-tools-header">
        <div class="dev-tools-header__brand">
          <div class="dev-tools-header__icon">
            <v-icon small color="white">mdi-console-line</v-icon>
          </div>
          <div>
            <div class="dev-tools-header__title">Dev Tools</div>
            <div class="dev-tools-header__subtitle">Database &amp; Function Routing</div>
          </div>
        </div>
        <v-btn icon small @click="setPanelOpen(false)" aria-label="Close">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </header>

      <!-- Stats strip -->
      <div class="dev-tools-stats">
        <div class="dev-tools-stat">
          <span class="dev-tools-stat__dot dev-tools-stat__dot--prod"></span>
          <span class="dev-tools-stat__value">{{ allFunctionNames.length - activeCount }}</span>
          <span class="dev-tools-stat__label">PROD</span>
        </div>
        <div class="dev-tools-stat">
          <span class="dev-tools-stat__dot dev-tools-stat__dot--local"></span>
          <span class="dev-tools-stat__value">{{ activeCount }}</span>
          <span class="dev-tools-stat__label">LOCAL</span>
        </div>
        <div class="dev-tools-stats__spacer"></div>
        <div
          v-if="activeCount > 0"
          class="dev-tools-stats__warning"
          :title="`Local functions read and write the ${activeDatabase === 'emulator' ? 'local emulator' : 'PRODUCTION'} Firestore/Auth/Storage`"
        >
          <v-icon size="14" color="#d97706">mdi-alert</v-icon>
          <span>uses {{ activeDatabase === 'emulator' ? 'EMULATOR' : 'PROD' }} data</span>
        </div>
      </div>

      <!-- Database (Firestore + Auth) -->
      <div class="dev-tools-section">
        <label class="dev-tools-label">Database</label>
        <div class="dev-tools-db">
          <button
            type="button"
            class="dev-tools-db__option"
            :class="{ 'dev-tools-db__option--active': database === 'prod' }"
            :disabled="envForcedEmulator"
            @click="setDatabase('prod')"
            :aria-pressed="database === 'prod'"
          >
            <v-icon size="14" left>mdi-cloud</v-icon>
            Production
          </button>
          <button
            type="button"
            class="dev-tools-db__option"
            :class="{ 'dev-tools-db__option--active': database === 'emulator' }"
            :disabled="envForcedEmulator"
            @click="setDatabase('emulator')"
            :aria-pressed="database === 'emulator'"
          >
            <v-icon size="14" left>mdi-laptop</v-icon>
            Emulator
          </button>
        </div>

        <template v-if="database === 'emulator'">
          <v-text-field
            :value="emulatorFirestore"
            @input="setEmulatorFirestore($event)"
            dense
            solo
            flat
            hide-details
            spellcheck="false"
            placeholder="Firestore host"
            class="dev-tools-input dev-tools-input--mono dev-tools-db__field"
            background-color="#f5f5f7"
            prepend-inner-icon="mdi-database-outline"
          ></v-text-field>
          <v-text-field
            :value="emulatorAuth"
            @input="setEmulatorAuth($event)"
            dense
            solo
            flat
            hide-details
            spellcheck="false"
            placeholder="Auth URL"
            class="dev-tools-input dev-tools-input--mono dev-tools-db__field"
            background-color="#f5f5f7"
            prepend-inner-icon="mdi-account-key-outline"
          ></v-text-field>
        </template>

        <div v-if="envForcedEmulator" class="dev-tools-db__note">
          Locked to the emulator by VUE_APP_USE_EMULATOR=1 on the dev server.
        </div>
        <div v-else-if="databaseReloadPending" class="dev-tools-db__pending">
          <span>
            Currently on <b>{{ activeDatabase === 'emulator' ? 'emulator' : 'production' }}</b>.
            Reload to switch — the auth session does not carry over, so you will have to sign in again.
          </span>
          <button type="button" class="dev-tools-action dev-tools-action--primary" @click="reloadForDatabase">
            <v-icon size="14" left>mdi-reload</v-icon>
            Reload
          </button>
        </div>
        <div v-else-if="activeDatabase === 'prod'" class="dev-tools-db__note dev-tools-db__note--warn">
          <v-icon size="14" color="#d97706">mdi-alert</v-icon>
          Connected to production Firestore — writes hit real customer data.
        </div>
      </div>

      <!-- Emulator URL -->
      <div class="dev-tools-section">
        <label class="dev-tools-label">Functions emulator endpoint</label>
        <v-text-field
          :value="localBase"
          @input="setLocalBase($event)"
          dense
          solo
          flat
          hide-details
          spellcheck="false"
          class="dev-tools-input dev-tools-input--mono"
          background-color="#f5f5f7"
          prepend-inner-icon="mdi-link-variant"
        ></v-text-field>
      </div>

      <!-- Search -->
      <div class="dev-tools-section dev-tools-section--search">
        <v-text-field
          v-model="search"
          placeholder="Search functions…"
          prepend-inner-icon="mdi-magnify"
          dense
          solo
          flat
          hide-details
          clearable
          class="dev-tools-input"
          background-color="#f5f5f7"
        ></v-text-field>
        <span class="dev-tools-counter">
          {{ filteredTotal }}<span class="dev-tools-counter__sep">/</span>{{ allFunctionNames.length }}
        </span>
      </div>

      <!-- Function list -->
      <div class="dev-tools-list">
        <template v-for="group in filteredGroups">
          <section
            v-if="group.functions.length > 0"
            :key="group.label"
            class="dev-tools-group"
          >
            <div class="dev-tools-group__header">
              <span class="dev-tools-group__label">{{ group.label }}</span>
              <span class="dev-tools-group__count">{{ group.functions.length }}</span>
            </div>
            <div
              v-for="name in group.functions"
              :key="name"
              class="dev-tools-row"
              :class="{ 'dev-tools-row--local': isLocal(name) }"
            >
              <span class="dev-tools-row__indicator"></span>
              <span class="dev-tools-row__name" :title="name">{{ name }}</span>
              <button
                type="button"
                class="dev-tools-toggle"
                :class="{ 'dev-tools-toggle--on': isLocal(name) }"
                @click="onToggle(name, isLocal(name) ? 'prod' : 'local')"
                :aria-pressed="isLocal(name)"
                :aria-label="`${isLocal(name) ? 'Disable' : 'Enable'} local override for ${name}`"
              >
                <span class="dev-tools-toggle__track">
                  <span class="dev-tools-toggle__thumb"></span>
                </span>
                <span class="dev-tools-toggle__label">{{ isLocal(name) ? 'LOCAL' : 'PROD' }}</span>
              </button>
            </div>
          </section>
        </template>
        <div v-if="filteredTotal === 0" class="dev-tools-empty">
          <v-icon color="#9ca3af" size="24">mdi-magnify-close</v-icon>
          <div>No functions match "{{ search }}"</div>
        </div>
      </div>

      <!-- Footer actions -->
      <footer class="dev-tools-footer">
        <button
          type="button"
          class="dev-tools-action dev-tools-action--ghost"
          :disabled="activeCount === 0"
          @click="setAllProd"
        >
          <v-icon size="14" left>mdi-cloud-outline</v-icon>
          Reset all
        </button>
        <button
          type="button"
          class="dev-tools-action dev-tools-action--primary"
          @click="setAllLocal"
        >
          <v-icon size="14" left>mdi-laptop</v-icon>
          All local
        </button>
      </footer>
    </v-card>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex"
import fc from "@/firebaseConfig"

// Functions called from the web client. Grouped for readability in the panel.
// Adding/removing a name here only affects the UI — the actual routing is
// decided by getFunctionUrl() at call time based on localStorage.
const FUNCTION_GROUPS = [
  {
    label: "PDF — Thai Smart Tax",
    functions: [
      "updateThaiSmartTaxPdf",
      "updateWHIncomeTaxReturn",
      "updateTaxIncomeReturn",
      "updateReportTax",
      "updateUnsignedPdf",
      "generateReportTaxAttachment",
      "generateTaxIncomReturnNetsuite",
      "generateWHTPdfNetsuite",
      "generateWHTIncomeTaxReturnNetsuite",
      "generateTaxReportPdf",
      "generateTaxReportPP36Pdf",
    ],
  },
  {
    label: "Excel",
    functions: [
      "generateTaxReportExcel",
      "generateTaxReportPP36Excel",
      "apiGenerateVATOutputExcel",
    ],
  },
  {
    label: "Email",
    functions: [
      "sendemail",
      "customerSendEmail",
      "sendFixedAssetsCountEmail",
      "apiSendABBTaxInfoEmailLink",
      "registerUserAndSendEmail",
    ],
  },
  {
    label: "API — Foxpro / SAP / Lab",
    functions: [
      "apiCreateDuplicateCompanies",
      "apiDeleteDuplicateCompanies",
      "apiExportToSAPLocal",
      "apiFoxproGetCompanyInfoBasePaths",
      "apiFoxproGetExpenseMappingBasePaths",
      "apiFoxproGetPaymentMappingBasePaths",
      "apiFoxproGetVendorsBasePaths",
      "apiFoxproTestBasePaths",
      "apiLaboratoryRun",
      "apiLaboratoryValidate",
    ],
  },
  {
    label: "API — Other",
    functions: [
      "apiPurchaseOrderStatusCount",
      "apiPurchaseFileTrackStatusCount",
      "apiResolveUserByEmail",
      "apiCompanyInvites",
      "apiSubmitABBTaxInfo",
      "apiGetFxBCEL",
      "apiGetFxKBANK",
    ],
  },
  {
    label: "Other",
    functions: [
      "createNewCompany",
      "onCheckTokenRegister",
      "saleOrdersImporter",
      "sendDoc",
      "updateCompanyPlan",
      "zipFiles",
    ],
  },
]

export default {
  name: "DevToolsPanel",
  data: () => ({
    search: "",
  }),
  computed: {
    ...mapState({
      panelOpen: (state) => state.devTools.panelOpen,
      localBase: (state) => state.devTools.localBase,
      database: (state) => state.devTools.database,
      emulatorFirestore: (state) => state.devTools.emulatorFirestore,
      emulatorAuth: (state) => state.devTools.emulatorAuth,
      user: (state) => state.auth && state.auth.user,
    }),
    ...mapGetters("devTools", ["isLocal", "activeCount"]),
    visible() {
      // Dev panel is dev-builds only. NODE_ENV is folded to a literal at
      // build time by Vue CLI, so this `if` becomes dead code in prod and
      // is eliminated by webpack/terser along with the entire template.
      return process.env.NODE_ENV !== "production"
    },
    // A dev server started with VUE_APP_USE_EMULATOR=1 (the Playwright run)
    // wins over the panel: the flag is baked in at build time.
    envForcedEmulator() {
      return process.env.VUE_APP_USE_EMULATOR === "1"
    },
    // What this page load actually connected to, as opposed to what is selected.
    activeDatabase() {
      return fc.usingEmulator ? "emulator" : "prod"
    },
    databaseReloadPending() {
      return !this.envForcedEmulator && this.database !== this.activeDatabase
    },
    allFunctionNames() {
      return FUNCTION_GROUPS.flatMap((g) => g.functions)
    },
    filteredGroups() {
      const q = (this.search || "").trim().toLowerCase()
      if (!q) return FUNCTION_GROUPS
      return FUNCTION_GROUPS.map((g) => ({
        label: g.label,
        functions: g.functions.filter((n) => n.toLowerCase().includes(q)),
      }))
    },
    filteredTotal() {
      return this.filteredGroups.reduce((sum, g) => sum + g.functions.length, 0)
    },
  },
  methods: {
    ...mapMutations("devTools", {
      setPanelOpen: "setPanelOpen",
      setLocalBaseMutation: "setLocalBase",
      setOverride: "setOverride",
      setAllMutation: "setAll",
      clearAll: "clearAll",
      setDatabase: "setDatabase",
      setEmulatorFirestore: "setEmulatorFirestore",
      setEmulatorAuth: "setEmulatorAuth",
    }),
    // Firestore only reads db.settings() before its first use, so the switch
    // cannot be applied to a running page — it lands on the next load.
    reloadForDatabase() {
      window.location.reload()
    },
    setLocalBase(value) {
      // ensure trailing slash
      const v = (value || "").trim()
      const normalized = v.endsWith("/") ? v : v + "/"
      this.setLocalBaseMutation(normalized)
    },
    onToggle(name, mode) {
      this.setOverride({ name, mode })
    },
    setAllProd() {
      this.clearAll()
    },
    setAllLocal() {
      this.setAllMutation({ names: this.allFunctionNames, mode: "local" })
    },
  },
}
</script>

<style scoped>
.dev-tools-db {
  display: flex;
  gap: 6px;
}
.dev-tools-db__option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 7px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f5f5f7;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
}
.dev-tools-db__option--active {
  background: #1e1e2e;
  border-color: #1e1e2e;
  color: #ffffff;
}
.dev-tools-db__option:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.dev-tools-db__field {
  margin-top: 6px;
}
.dev-tools-db__note {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.4;
  color: #6b7280;
}
.dev-tools-db__note--warn {
  color: #b45309;
}
.dev-tools-db__pending {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding: 7px 8px;
  border-radius: 8px;
  background: #fef3c7;
  color: #92400e;
  font-size: 11px;
  line-height: 1.4;
}
.dev-tools-db__pending .dev-tools-action {
  flex: 0 0 auto;
}

.dev-tools-panel {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, sans-serif;
  color: #1e1e2e;
}

/* ---------- Collapsed bubble ---------- */
.dev-tools-bubble {
  border-radius: 999px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1);
  letter-spacing: 0.05em;
  font-weight: 600 !important;
  text-transform: none !important;
  padding: 0 16px !important;
  height: 36px !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.dev-tools-bubble:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1);
}
.dev-tools-bubble__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 8px;
  padding: 0 5px;
  background: #f59e0b;
  color: #1e1e2e;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  font-feature-settings: "tnum";
}

/* ---------- Expanded card ---------- */
.dev-tools-card {
  width: 420px;
  max-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  border-radius: 14px !important;
  border: 1px solid rgba(0, 0, 0, 0.04);
  overflow: hidden !important;
  background: #ffffff !important;
}

/* ---------- Header ---------- */
.dev-tools-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #1e1e2e 0%, #2d2d44 100%);
  color: #ffffff;
}
.dev-tools-header__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.dev-tools-header__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}
.dev-tools-header__title {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
}
.dev-tools-header__subtitle {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 400;
  margin-top: 1px;
}
.dev-tools-header :deep(.v-btn) {
  color: rgba(255, 255, 255, 0.7) !important;
}
.dev-tools-header :deep(.v-btn:hover) {
  color: #ffffff !important;
}

/* ---------- Stats strip ---------- */
.dev-tools-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  background: #fafafa;
  border-bottom: 1px solid #ececf0;
  font-size: 11px;
}
.dev-tools-stat {
  display: flex;
  align-items: center;
  gap: 6px;
}
.dev-tools-stat__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
}
.dev-tools-stat__dot--prod { background: #10b981; }
.dev-tools-stat__dot--local { background: #f59e0b; }
.dev-tools-stat__value {
  font-weight: 600;
  font-size: 12px;
  font-feature-settings: "tnum";
  color: #1e1e2e;
}
.dev-tools-stat__label {
  color: #6b7280;
  font-weight: 500;
  letter-spacing: 0.04em;
}
.dev-tools-stats__spacer { flex: 1; }
.dev-tools-stats__warning {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #b45309;
  font-weight: 500;
}

/* ---------- Sections ---------- */
.dev-tools-section {
  padding: 12px 16px;
  border-bottom: 1px solid #ececf0;
}
.dev-tools-section--search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
}
.dev-tools-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin-bottom: 6px;
}
.dev-tools-input :deep(.v-input__slot) {
  min-height: 32px !important;
  padding: 0 10px !important;
  border-radius: 8px !important;
}
.dev-tools-input :deep(input) {
  font-size: 12px !important;
}
.dev-tools-input--mono :deep(input) {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace !important;
  font-size: 11px !important;
}
.dev-tools-input :deep(.v-icon) { font-size: 16px !important; }
.dev-tools-counter {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  font-feature-settings: "tnum";
  white-space: nowrap;
  padding: 4px 8px;
  background: #f5f5f7;
  border-radius: 6px;
}
.dev-tools-counter__sep {
  color: #d1d5db;
  margin: 0 2px;
  font-weight: 400;
}

/* ---------- List ---------- */
.dev-tools-list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0;
  max-height: 48vh;
}
.dev-tools-list::-webkit-scrollbar { width: 6px; }
.dev-tools-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 999px;
}
.dev-tools-list::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

.dev-tools-group {
  padding: 6px 0;
}
.dev-tools-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px 4px;
}
.dev-tools-group__label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
}
.dev-tools-group__count {
  font-size: 10px;
  color: #9ca3af;
  font-feature-settings: "tnum";
}

.dev-tools-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px 6px 12px;
  position: relative;
  transition: background 0.12s ease;
}
.dev-tools-row:hover { background: #fafafa; }
.dev-tools-row__indicator {
  width: 3px;
  height: 16px;
  border-radius: 999px;
  background: transparent;
  transition: background 0.12s ease;
}
.dev-tools-row--local .dev-tools-row__indicator { background: #f59e0b; }
.dev-tools-row--local { background: #fffbf3; }
.dev-tools-row--local:hover { background: #fff5e1; }

.dev-tools-row__name {
  flex: 1;
  font-family: ui-monospace, SFMono-Regular, Menlo, "Cascadia Mono", monospace;
  font-size: 11.5px;
  color: #1e1e2e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

/* ---------- iOS-style toggle ---------- */
.dev-tools-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
}
.dev-tools-toggle:focus-visible .dev-tools-toggle__track {
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.4);
}
.dev-tools-toggle__track {
  position: relative;
  width: 30px;
  height: 16px;
  background: #d1d5db;
  border-radius: 999px;
  transition: background 0.15s ease;
}
.dev-tools-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 12px;
  height: 12px;
  background: #ffffff;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease;
}
.dev-tools-toggle__label {
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #9ca3af;
  min-width: 36px;
  text-align: left;
  font-feature-settings: "tnum";
}
.dev-tools-toggle--on .dev-tools-toggle__track { background: #f59e0b; }
.dev-tools-toggle--on .dev-tools-toggle__thumb {
  transform: translateX(14px);
}
.dev-tools-toggle--on .dev-tools-toggle__label { color: #b45309; }

/* ---------- Empty state ---------- */
.dev-tools-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  color: #9ca3af;
  font-size: 12px;
}

/* ---------- Footer ---------- */
.dev-tools-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #fafafa;
  border-top: 1px solid #ececf0;
}
.dev-tools-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  outline: none;
  transition: background 0.12s ease, color 0.12s ease, transform 0.08s ease;
  font-family: inherit;
}
.dev-tools-action:active { transform: translateY(1px); }
.dev-tools-action:focus-visible {
  box-shadow: 0 0 0 2px rgba(30, 30, 46, 0.2);
}
.dev-tools-action--ghost {
  background: transparent;
  color: #6b7280;
}
.dev-tools-action--ghost:hover:not(:disabled) {
  background: #ececf0;
  color: #1e1e2e;
}
.dev-tools-action--ghost:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.dev-tools-action--primary {
  background: #1e1e2e;
  color: #ffffff;
}
.dev-tools-action--primary:hover {
  background: #2d2d44;
}
</style>
